/**
 * Optimistic mutation with real rollback, and universal undo.
 *
 * Two properties do the work:
 *
 *  1. Every operation carries a precomputed INVERSE. Undo is then just applying
 *     the inverse, which means it works for actions the server has already
 *     accepted, and it works offline.
 *  2. Operations are per-flag DELTAS, never whole flag sets. Two family members
 *     triaging the same thread on different devices then merge instead of
 *     clobbering: `+\Seen` and `+\Flagged` commute, `flags = {...}` does not.
 */

export type OpKind = 'flag' | 'label' | 'move'

export interface FlagOp {
  readonly kind: 'flag'
  readonly mailbox: string
  readonly uid: number
  readonly add: readonly string[]
  readonly remove: readonly string[]
}

export interface LabelOp {
  readonly kind: 'label'
  readonly guid: string
  readonly add: readonly string[]
  readonly remove: readonly string[]
}

export interface MoveOp {
  readonly kind: 'move'
  readonly mailbox: string
  readonly uid: number
  readonly to: string
  readonly from: string
  /**
   * The UID the message has in `to`, once the server has said so.
   *
   * ABSENT AT ENQUEUE TIME BY CONSTRUCTION, and that is the whole difficulty.
   * IMAP UIDs are per-mailbox: a MOVE gives the message a new one in the
   * destination, and the only thing that reveals it is the UIDPLUS `COPYUID`
   * response, which arrives with the server's answer — after the optimistic
   * update has already been applied locally. It is filled in by `ack`.
   */
  readonly landedUid?: number
}

export type Operation = FlagOp | LabelOp | MoveOp

export interface PendingOp {
  readonly id: string
  readonly op: Operation
  /** `null` while the server has not yet said where a move landed. See `invert`. */
  readonly inverse: Operation | null
  readonly label: string
  /** Every operation from one user action shares this. See `ToastState.batchId`. */
  readonly batch: string
  readonly createdAt: number
  readonly attempts: number
  readonly state: 'pending' | 'acked' | 'failed'
}

/**
 * The inverse of an operation — or `null` when it is not knowable yet.
 *
 * `null` HAPPENS FOR EXACTLY ONE CASE, AND IT USED TO BE A WRONG ANSWER INSTEAD.
 * This returned `{kind:'move', mailbox: op.to, uid: op.uid, ...}` for a move:
 * the source UID, reused as if it addressed the same message in the destination
 * mailbox. IMAP UIDs are per-mailbox, so that identifies whichever unrelated
 * message happens to hold that UID in the destination — and the undo of "archive
 * this" would drag a stranger's message back into the inbox. It was latent only
 * because ops never reached IMAP at all.
 *
 * The destination UID cannot be computed, guessed, or derived; only the server's
 * UIDPLUS `COPYUID` reveals it, and it arrives after the optimistic update. So
 * the honest answer at enqueue time is "no inverse yet", and `ack` supplies one
 * when the server says where the message landed. A move whose server sent no
 * COPYUID keeps `null` for ever, which is correct: it is genuinely not undoable,
 * and the UI must say so rather than offer a button that moves the wrong mail.
 */
export function invert(op: Operation): Operation | null {
  switch (op.kind) {
    case 'flag':
      return { kind: 'flag', mailbox: op.mailbox, uid: op.uid, add: op.remove, remove: op.add }
    case 'label':
      return { kind: 'label', guid: op.guid, add: op.remove, remove: op.add }
    case 'move':
      return op.landedUid === undefined
        ? null
        : { kind: 'move', mailbox: op.to, uid: op.landedUid, to: op.from, from: op.to }
  }
}

/** Operations commute when they touch disjoint flags on the same target. */
export function commutes(a: Operation, b: Operation): boolean {
  if (a.kind !== b.kind) return true
  if (a.kind === 'flag' && b.kind === 'flag') {
    if (a.mailbox !== b.mailbox || a.uid !== b.uid) return true
    const touched = (op: FlagOp) => new Set([...op.add, ...op.remove])
    const left = touched(a)
    return ![...touched(b)].some((flag) => left.has(flag))
  }
  if (a.kind === 'label' && b.kind === 'label') {
    if (a.guid !== b.guid) return true
    const touched = (op: LabelOp) => new Set([...op.add, ...op.remove])
    const left = touched(a)
    return ![...touched(b)].some((keyword) => left.has(keyword))
  }
  return false
}

export const UNDO_WINDOW_SECONDS = 10

/**
 * The label that means "the reader did not ask for this, so do not offer it
 * back to them".
 *
 * IT EXISTS FOR AUTO MARK-READ AND IT IS NOT COSMETIC. `toast()` returns the
 * NEWEST non-failed batch for the whole ten-second undo window, so an operation
 * the client performs on its own — marking the open message `\Seen` two seconds
 * after it appears — would both pop "Marked 1 read" on every cursor rest AND
 * *replace* the Undo for the archive the reader did a second earlier, inside
 * the window in which they might still want it back. Silencing the toast is
 * what keeps a thing nobody pressed from stealing the undo for a thing they
 * did.
 *
 * A SENTINEL LABEL RATHER THAN A FLAG ON `enqueue`, because the label is the
 * only channel that already runs from the caller to here — and because a
 * silenced op is otherwise an ordinary one: it still queues, still patches
 * optimistically, still rolls back on refusal, and still holds an inverse.
 */
export const SILENT_LABEL = 'hearth.silent'

export interface ToastState {
  readonly label: string
  /**
   * The BATCH, not one operation.
   *
   * This was `opId`, and with it Undo took back exactly one message of however
   * many the action touched: "Archived 12 conversations" followed by an Undo
   * that restored one, with no indication that the other eleven were still
   * gone. One keystroke is one action to the person who pressed it, whatever it
   * expands to on the wire.
   */
  readonly batchId: string
  /** How many operations the batch holds. `1` for the ordinary single-row case. */
  readonly count: number
  readonly expiresAt: number
  /**
   * Whether Undo can be offered yet.
   *
   * A move is briefly NOT undoable — between the optimistic update and the
   * server's COPYUID — and permanently not undoable if that COPYUID never came.
   * The UI reads this rather than inspecting the op, so "the button is there but
   * does nothing" is not a state it can render.
   *
   * FOR A BATCH IT IS ALL-OR-NOTHING. An Undo that restored nine of twelve
   * messages and said nothing about the other three would be worse than one
   * that refuses: the reader would believe the action was taken back. So a
   * batch is undoable only when every operation in it has an inverse.
   */
  readonly undoable: boolean
}

/**
 * The mutation queue.
 *
 * Deliberately not a general task runner: it holds the operations the user has
 * performed but the server has not yet acknowledged, offers exactly one undo
 * (the most recent), and drains in order on reconnect.
 */
export class OperationQueue {
  private readonly ops = new Map<string, PendingOp>()
  private order: string[] = []
  private counter = 0

  private readonly now: () => number

  constructor(now: () => number) {
    this.now = now
  }

  get pending(): PendingOp[] {
    return this.order.map((id) => this.ops.get(id)!).filter((op) => op.state === 'pending')
  }

  get all(): PendingOp[] {
    return this.order.map((id) => this.ops.get(id)!)
  }

  private nextId(): string {
    this.counter += 1
    return `op-${this.counter}`
  }

  /** A fresh batch id, for a caller about to enqueue one action's worth of ops. */
  nextBatch(): string {
    this.counter += 1
    return `batch-${this.counter}`
  }

  /**
   * Record an operation as applied locally and awaiting the server.
   *
   * `batch` groups the operations one keystroke produced. Omitted, the op is its
   * own batch, which is the right answer for anything genuinely single.
   */
  enqueue(op: Operation, label: string, batch?: string): PendingOp {
    const id = this.nextId()
    const pending: PendingOp = {
      id,
      op,
      inverse: invert(op),
      label,
      batch: batch ?? id,
      createdAt: this.now(),
      attempts: 0,
      state: 'pending',
    }
    this.ops.set(pending.id, pending)
    this.order.push(pending.id)
    return pending
  }

  /** Every operation in one batch, in the order they were enqueued. */
  batch(batchId: string): PendingOp[] {
    return this.order.map((id) => this.ops.get(id)!).filter((op) => op.batch === batchId)
  }

  /**
   * The server accepted the op — and, for a move, said where it landed.
   *
   * THE LANDING IS WHY THIS TAKES AN ARGUMENT. `/ops` answers a move with
   * `moved: {mailbox, uidvalidity, uid}` derived from UIDPLUS `COPYUID`, and
   * that is the first moment anything in the system knows the destination UID.
   * Recording it here is what turns a move from "not undoable" into "undoable",
   * and computing the inverse from it is what stops undo aiming at whichever
   * message happens to hold the old UID in the destination mailbox.
   *
   * A move acked WITHOUT a landing stays un-undoable forever, deliberately —
   * the server moved the message and did not say where, so there is no honest
   * inverse to offer.
   */
  ack(id: string, landing?: { readonly uid: number }): void {
    const op = this.ops.get(id)
    if (!op) return
    if (landing && op.op.kind === 'move') {
      const landed: MoveOp = { ...op.op, landedUid: landing.uid }
      this.ops.set(id, { ...op, op: landed, inverse: invert(landed), state: 'acked' })
      return
    }
    this.ops.set(id, { ...op, state: 'acked' })
  }

  fail(id: string): PendingOp | null {
    const op = this.ops.get(id)
    if (!op) return null
    const failed: PendingOp = { ...op, state: 'failed', attempts: op.attempts + 1 }
    this.ops.set(id, failed)
    return failed
  }

  /** The single toast the UI shows. Only the most recent action is undoable. */
  toast(): ToastState | null {
    for (let i = this.order.length - 1; i >= 0; i--) {
      const op = this.ops.get(this.order[i]!)!
      if (op.state === 'failed') continue
      // SKIPPED, NOT STOPPED AT. A silent op is passed over so the batch behind
      // it — an archive the reader might still want back — keeps the toast for
      // the rest of its window. Returning null here instead would let auto
      // mark-read cancel somebody's Undo just by existing.
      if (op.label === SILENT_LABEL) continue
      const expiresAt = op.createdAt + UNDO_WINDOW_SECONDS
      if (this.now() >= expiresAt) return null
      // THE WHOLE BATCH, found from whichever of its operations was newest.
      // `undoable` is every one of them, because a partial undo that says
      // nothing about what it left behind is worse than a refusal.
      const members = this.batch(op.batch).filter((member) => member.state !== 'failed')
      return {
        label: op.label,
        batchId: op.batch,
        count: members.length,
        expiresAt,
        undoable: members.length > 0 && members.every((member) => member.inverse !== null),
      }
    }
    return null
  }

  /**
   * Undo by enqueueing the inverse rather than by rewinding state. The inverse
   * is a real operation the server will accept, so undo behaves identically
   * whether the original was acknowledged, still in flight, or queued offline.
   */
  undo(id: string): PendingOp | null {
    const op = this.ops.get(id)
    if (!op || op.state === 'failed') return null
    // NOT UNDOABLE IS A REAL ANSWER, not a failure to look one up: a move the
    // server has not yet acked, or acked without a COPYUID, has no inverse that
    // names the right message. Returning null is what lets the caller leave the
    // button disabled instead of moving somebody else's mail.
    if (op.inverse === null) return null
    return this.enqueue(op.inverse, `Undo ${op.label.toLowerCase()}`)
  }

  /**
   * Undo a whole batch: one inverse per operation, in reverse order.
   *
   * REVERSE, because the inverses are applied in sequence and the operations
   * were not necessarily independent of one another. It costs nothing when they
   * were, which is the common case.
   *
   * ALL OR NOTHING. If any member has no inverse the answer is null and the
   * caller offers no Undo — the same rule `toast().undoable` reports, in the
   * same place, so the button and the action cannot disagree.
   */
  undoBatch(batchId: string): PendingOp[] | null {
    const members = this.batch(batchId).filter((op) => op.state !== 'failed')
    if (members.length === 0) return null
    if (members.some((op) => op.inverse === null)) return null
    const undoBatchId = this.nextBatch()
    return [...members]
      .reverse()
      .map((op) => this.enqueue(op.inverse!, `Undo ${op.label.toLowerCase()}`, undoBatchId))
  }

  /** Drop acknowledged operations older than the undo window. */
  prune(): void {
    const cutoff = this.now() - UNDO_WINDOW_SECONDS
    this.order = this.order.filter((id) => {
      const op = this.ops.get(id)!
      if (op.state === 'acked' && op.createdAt < cutoff) {
        this.ops.delete(id)
        return false
      }
      return true
    })
  }
}

export interface ChangeHint {
  readonly type: string
  readonly seq: number
}

/**
 * Reconcile the local sequence against a hint from the server.
 *
 * The WebSocket frame carries a sequence number and nothing else, so every way
 * of falling behind — a dropped socket, a cold boot, a Worker deploy that
 * disconnects everyone, a two-week holiday — produces the same question ("what
 * changed after N?") and takes the same already-tested code path.
 */
export function catchUpNeeded(localSeq: number, hint: ChangeHint): boolean {
  return hint.seq > localSeq
}
